import {
  AppFunctionComponent,
  ListBuilder,
  QueryResultFlat,
} from "../../../../../types"
import React, { useState } from "react"
import { QuickStartProps, QuickStarterQueryData } from "./quick-start.interface"
import { graphql, useStaticQuery } from "gatsby"
import TextWithEmbedResolver from "../../../../../resolvers/text-with-embed/text-with-embed.resolver"
import {
  QuickStartSectionWrapper,
  TextWithEmbedStyle,
} from "./quick-start.styled"
import { useLocalizationContext } from "../../../../../context/localization.context"
import QuickStartRevealList from "./quick-start-reveal-list.component"
import QuickStartManualsTable from "./quick-start-manuals-table.component"
import QuickStartHeadingText from "./quick-start-heading-text.component"
import QuickStartBreathingEmbed from "./quick-start-breathing-embed.component"

function resolvePolishVersion<T extends { node_locale?: string }>(
  obj: QueryResultFlat<T>,
  locale: string
): T {
  return obj.nodes.find((el) => el.node_locale === locale) || obj.nodes[0]
}

const transformListObject = (obj: ListBuilder, hash: string) => {
  return { hash, ...obj }
}

const QuickStart: AppFunctionComponent<QuickStartProps> = ({ config }) => {
  const {
    modules: { manuals },
  } = config

  const {
    list_one,
    list_two,
    list_three,
    list_four,
    contentfulText,
    changeStrap_1,
    changeStrap_2,
    howBreath,
    howBreath_2,
    howBreath_3,
    howBreath_4,
    howBreath_5,
    cleanStraps_1,
    cleanStraps_2,
    download_1,
  } = useStaticQuery<QuickStarterQueryData>(query)
  const { locale } = useLocalizationContext()

  const listOne = resolvePolishVersion(list_one, locale)
  const listTwo = resolvePolishVersion(list_two, locale)
  const listThree = resolvePolishVersion(list_three, locale)
  const listFour = resolvePolishVersion(list_four, locale)
  const headingText = resolvePolishVersion(contentfulText, locale)
  const changeStrap1 = resolvePolishVersion(changeStrap_1, locale)
  const changeStrap2 = resolvePolishVersion(changeStrap_2, locale)
  const howBreath1 = resolvePolishVersion(howBreath, locale)
  const howBreath2 = resolvePolishVersion(howBreath_2, locale)
  const howBreath3 = resolvePolishVersion(howBreath_3, locale)
  const howBreath4 = resolvePolishVersion(howBreath_4, locale)
  const howBreath5 = resolvePolishVersion(howBreath_5, locale)
  const cleanStraps1 = resolvePolishVersion(cleanStraps_1, locale)
  const cleanStraps2 = resolvePolishVersion(cleanStraps_2, locale)
  const download1 = resolvePolishVersion(download_1, locale)

  const listItems = [
    transformListObject(listOne, "#change-straps"),
    transformListObject(listTwo, "#breathing-functions"),
    transformListObject(listThree, "#clean-straps"),
    transformListObject(listFour, "#download-manual"),
  ]

  const [revealedSectionId, setRevealedSectionId] = useState<string | null>(
    null
  )

  return (
    <>
      <QuickStartHeadingText headingText={headingText} />
      <QuickStartRevealList
        revealList={listItems}
        revealedSectionId={revealedSectionId}
        setRevealedSectionId={setRevealedSectionId}
        grayscale
      />
      <QuickStartSectionWrapper
        id="#change-straps"
        revealedSectionId={revealedSectionId}
      >
        <TextWithEmbedResolver {...changeStrap1} />
        <TextWithEmbedResolver {...changeStrap2} />
      </QuickStartSectionWrapper>
      <QuickStartSectionWrapper
        id="#breathing-functions"
        revealedSectionId={revealedSectionId}
      >
        <TextWithEmbedStyle>
          <TextWithEmbedResolver {...howBreath1} />
        </TextWithEmbedStyle>
        <QuickStartBreathingEmbed
          sectionOne={howBreath2}
          sectionTwo={howBreath3}
          sectionThree={howBreath4}
          sectionFour={howBreath5}
        />
      </QuickStartSectionWrapper>
      <QuickStartSectionWrapper
        id="#clean-straps"
        revealedSectionId={revealedSectionId}
      >
        <TextWithEmbedResolver {...cleanStraps1} />
        <TextWithEmbedResolver {...cleanStraps2} />
      </QuickStartSectionWrapper>
      <QuickStartSectionWrapper
        id="#download-manual"
        revealedSectionId={revealedSectionId}
      >
        <QuickStartHeadingText headingText={download1} />
        <QuickStartManualsTable manuals={manuals} />
      </QuickStartSectionWrapper>
    </>
  )
}

export default QuickStart

export const query = graphql`
  query QuickStartSliderQuery {
    contentfulText: allContentfulText(
      filter: { entryName: { eq: "Get started with how to guides" } }
    ) {
      nodes {
        ...Text
        node_locale
      }
    }
    list_one: allContentfulListItem(
      filter: { entryName: { eq: "How to change a strap" } }
    ) {
      nodes {
        ...ListItem
        node_locale
      }
    }
    list_two: allContentfulListItem(
      filter: { entryName: { eq: "How to use breathing function" } }
    ) {
      nodes {
        ...ListItem
        node_locale
      }
    }
    list_three: allContentfulListItem(
      filter: { entryName: { eq: "How to clean the straps" } }
    ) {
      nodes {
        ...ListItem
        node_locale
      }
    }
    list_four: allContentfulListItem(
      filter: { entryName: { eq: "Download PDF manual" } }
    ) {
      nodes {
        ...ListItem
        node_locale
      }
    }
    changeStrap_1: allContentfulTextWithEmbed(
      filter: { entryName: { eq: "How to change the strap" } }
    ) {
      nodes {
        ...TextWithEmbed
        node_locale
      }
    }
    changeStrap_2: allContentfulTextWithEmbed(
      filter: { entryName: { eq: "To remove and fit the straps:" } }
    ) {
      nodes {
        ...TextWithEmbed
        node_locale
      }
    }
    howBreath: allContentfulTextWithEmbed(
      filter: { entryName: { eq: "How to breathe with the Mudita Moment" } }
    ) {
      nodes {
        ...TextWithEmbed
        node_locale
      }
    }
    howBreath_2: allContentfulTextWithEmbed(
      filter: {
        entryName: {
          eq: "Take a closer look at the dotted pattern on the watch."
        }
      }
    ) {
      nodes {
        ...TextWithEmbed
        node_locale
      }
    }
    howBreath_3: allContentfulText(
      filter: { entryName: { eq: "Mindfull breathing:" } }
    ) {
      nodes {
        ...Text
        node_locale
      }
    }
    howBreath_4: allContentfulText(
      filter: { entryName: { eq: "Square breathing:" } }
    ) {
      nodes {
        ...Text
        node_locale
      }
    }
    howBreath_5: allContentfulText(
      filter: { entryName: { eq: "Slow breathing:" } }
    ) {
      nodes {
        ...Text
        node_locale
      }
    }
    cleanStraps_1: allContentfulTextWithEmbed(
      filter: { entryName: { eq: "Desserto® watch strap" } }
    ) {
      nodes {
        ...TextWithEmbed
        node_locale
      }
    }
    cleanStraps_2: allContentfulTextWithEmbed(
      filter: {
        entryName: { eq: "How to take care of your Desserto® watch strap?" }
      }
    ) {
      nodes {
        ...TextWithEmbed
        node_locale
      }
    }
    download_1: allContentfulText(
      filter: { entryName: { eq: "Download Your Ultimate Guide" } }
    ) {
      nodes {
        ...Text
        node_locale
      }
    }
  }
`
